import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import ObfuscatedEmail from '../components/ObfuscatedEmail';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Clock, Send, Check, Pencil, X, Bold, Italic } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

// ---------------- Inline plain-text editor (phone / email / address) ----------------
const InlineTextEditor = ({ initialValue, onSave, onCancel, placeholder, testid }) => {
  const [value, setValue] = useState(initialValue || '');
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    if (value === initialValue) { onCancel(); return; }
    setSaving(true); await onSave(value); setSaving(false);
  };
  return (
    <div className="flex items-center gap-2 mt-1">
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); handleSave(); }
          if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
        }}
        placeholder={placeholder}
        className="rounded-none h-8 text-sm flex-1"
        data-testid={`${testid}-input`}
        autoFocus
      />
      <Button size="sm" className="rounded-none h-8 px-2" disabled={saving} data-testid={`${testid}-save`}
        onMouseDown={(e) => e.preventDefault()}
        onClick={handleSave}>
        <Check className="w-3.5 h-3.5" />
      </Button>
      <Button size="sm" variant="ghost" className="rounded-none h-8 px-2"
        onMouseDown={(e) => e.preventDefault()}
        onClick={onCancel} data-testid={`${testid}-cancel`}>
        <X className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
};

// ---------------- Inline rich-text editor (hours: B/I + size + color) ----------------
const TEXT_SIZES = [
  { label: 'S', value: '0.875rem' },
  { label: 'M', value: '1rem' },
  { label: 'L', value: '1.125rem' },
];
const TEXT_COLORS = ['#111111', '#6b7280', '#3c8ef2', '#22c55e', '#ef4444'];

const InlineRichEditor = ({ initialHtml, onSave, onCancel, testid }) => {
  const editorRef = useRef(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editorRef.current) editorRef.current.innerHTML = initialHtml || '';
  }, [initialHtml]);

  const exec = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
  };

  return (
    <div className="mt-1 border border-border bg-white" data-testid={`${testid}-rich`}>
      <div className="flex items-center gap-1 px-2 py-1 border-b border-border bg-gray-50">
        <button type="button" onClick={() => exec('bold')} className="p-1.5 hover:bg-gray-200 rounded" title="Gras" data-testid={`${testid}-bold`}>
          <Bold className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={() => exec('italic')} className="p-1.5 hover:bg-gray-200 rounded" title="Italique" data-testid={`${testid}-italic`}>
          <Italic className="w-3.5 h-3.5" />
        </button>
        <div className="w-px h-4 bg-gray-300 mx-1" />
        {TEXT_SIZES.map((s) => (
          <button key={s.label} type="button" onClick={() => exec('fontSize', '7') || setTimeout(() => {
            // execCommand fontSize uses 1-7; we override with inline style for px control
            const sel = window.getSelection();
            if (!sel.rangeCount) return;
            document.querySelectorAll('font[size="7"]').forEach((f) => {
              const span = document.createElement('span');
              span.style.fontSize = s.value;
              span.innerHTML = f.innerHTML;
              f.parentNode.replaceChild(span, f);
            });
          }, 0)}
            className="px-1.5 py-0.5 text-xs hover:bg-gray-200 rounded font-semibold" title={`Taille ${s.label}`} data-testid={`${testid}-size-${s.label}`}>
            {s.label}
          </button>
        ))}
        <div className="w-px h-4 bg-gray-300 mx-1" />
        {TEXT_COLORS.map((c) => (
          <button key={c} type="button" onClick={() => exec('foreColor', c)}
            className="w-4 h-4 rounded-full border border-gray-300 hover:scale-110 transition-transform"
            style={{ backgroundColor: c }} title={`Couleur ${c}`} data-testid={`${testid}-color-${c}`} />
        ))}
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        className="p-3 text-sm min-h-[60px] focus:outline-none"
        data-testid={`${testid}-editor`}
      />
      <div className="flex items-center gap-2 px-2 py-1 border-t border-border bg-gray-50">
        <Button size="sm" className="rounded-none h-7 px-2 text-xs" disabled={saving} data-testid={`${testid}-save`}
          onClick={async () => { setSaving(true); await onSave(editorRef.current?.innerHTML || ''); setSaving(false); }}>
          <Check className="w-3 h-3 mr-1" /> Enregistrer
        </Button>
        <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 text-xs" onClick={onCancel} data-testid={`${testid}-cancel`}>
          Annuler
        </Button>
      </div>
    </div>
  );
};

// ---------------- ContactPage ----------------
const ContactPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [formData, setFormData] = useState({ name: '', email: '', company: '', phone: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Editable appearance fields (loaded from /api/admin/settings)
  const [info, setInfo] = useState({
    phone: '+33 1 45 85 81 76',
    email: '',
    address: 'Paris, France',
    hours: 'Lun-Ven : retrait/retour 9h30-12h30 et 13h30-18h30 — planning 10h-12h30 et 14h-18h30',
  });
  const [editing, setEditing] = useState(null); // 'phone' | 'email' | 'address' | 'hours' | null

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API_BASE}/admin/settings`)
      .then((r) => r.json())
      .then((data) => {
        const a = (data && data.appearance) || {};
        setInfo({
          phone: a.footer_phone || '+33 1 45 85 81 76',
          email: a.footer_email || '',
          address: a.footer_address || 'Paris, France',
          hours: a.footer_hours || 'Lun-Ven : retrait/retour 9h30-12h30 et 13h30-18h30 — planning 10h-12h30 et 14h-18h30',
        });
      })
      .catch(() => { /* keep defaults */ });
  }, []);

  // Persist a single field via PUT /api/admin/settings/appearance
  const saveField = async (key, value) => {
    const next = { ...info, [key]: value };
    setInfo(next);
    setEditing(null);
    try {
      const cur = await fetch(`${API_BASE}/admin/settings`, { credentials: 'include' }).then((r) => r.json());
      const appearance = { ...(cur?.appearance || {}) };
      appearance[`footer_${key}`] = value;
      const res = await fetch(`${API_BASE}/admin/settings/appearance`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(appearance),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(`${key === 'hours' ? 'Horaires' : key === 'phone' ? 'Téléphone' : key === 'email' ? 'Email' : 'Adresse'} mis à jour`, {
        description: 'Le changement apparaît partout (Footer, Header, Contact).',
        duration: 3000,
      });
    } catch (e) {
      console.error('Save failed', e);
      toast.error('Échec de la sauvegarde', { description: e.message || 'Vérifie que tu es bien connecté en admin.' });
      // Revert local state since save failed
      setInfo(info);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setSuccess(true);
    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen py-16" data-testid="contact-success">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-success" />
            </div>
            <h1 className="text-3xl font-extrabold mb-4">{t('contact.success_title')}</h1>
            <p className="text-muted-foreground mb-8">{t('contact.success_desc')}</p>
            <Button onClick={() => setSuccess(false)} variant="outline" className="rounded-none">
              {t('contact.send_another')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Cartouche definitions
  const cartouches = [
    { key: 'phone', icon: Phone, label: t('contact.phone'), value: info.phone, kind: 'text', href: `tel:${(info.phone || '').replace(/[^+0-9]/g, '')}` },
    { key: 'email', icon: Mail, label: t('contact.email'), value: info.email, kind: 'email' },
    { key: 'address', icon: MapPin, label: t('contact.address'), value: info.address, kind: 'text' },
    { key: 'hours', icon: Clock, label: t('contact.hours'), value: info.hours, kind: 'rich' },
  ];

  return (
    <div className="min-h-screen py-8" data-testid="contact-page">
      <SEO
        title="Contact"
        description="Contactez Puzzle Video pour vos projets de location de matériel audiovisuel. Devis gratuit, conseil personnalisé. Tél : +33 1 45 85 81 76."
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          name: 'Contact Puzzle Video',
          description: 'Contactez notre équipe pour un devis de location de matériel audiovisuel',
        }}
      />
      <div className="container mx-auto px-6 md:px-12 lg:px-24">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-2">{t('contact.subtitle')}</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-8">{t('contact.title')}</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="space-y-8">
            <div>
              <h2 className="font-semibold text-lg mb-4">{t('contact.info_title')}</h2>
              <p className="text-muted-foreground leading-relaxed">{t('contact.info_desc')}</p>
            </div>

            <div className="space-y-4">
              {cartouches.map((item) => {
                const isEditing = editing === item.key;
                return (
                  <Card key={item.key} className="border border-border rounded-none" data-testid={`cartouche-${item.key}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <item.icon className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-xs uppercase tracking-wider text-muted-foreground">{item.label}</p>
                            {isAdmin && !isEditing && (
                              <button
                                onClick={() => setEditing(item.key)}
                                className="p-1 hover:bg-gray-100 rounded text-muted-foreground hover:text-primary transition-colors"
                                title="Modifier"
                                data-testid={`edit-${item.key}`}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>

                          {/* VIEW MODE */}
                          {!isEditing && item.kind === 'email' && <ObfuscatedEmail email={item.value} className="font-medium hover:text-primary transition-colors" testId="contact-email" />}
                          {!isEditing && item.kind === 'text' && item.href && (
                            <a href={item.href} className="font-medium hover:text-primary transition-colors">{item.value}</a>
                          )}
                          {!isEditing && item.kind === 'text' && !item.href && (
                            <p className="font-medium">{item.value}</p>
                          )}
                          {!isEditing && item.kind === 'rich' && (
                            <div className="font-medium text-sm" dangerouslySetInnerHTML={{ __html: item.value }} />
                          )}

                          {/* EDIT MODE */}
                          {isEditing && (item.kind === 'text' || item.kind === 'email') && (
                            <InlineTextEditor
                              initialValue={item.value}
                              placeholder={item.label}
                              testid={`field-${item.key}`}
                              onCancel={() => setEditing(null)}
                              onSave={(v) => saveField(item.key, v)}
                            />
                          )}
                          {isEditing && item.kind === 'rich' && (
                            <InlineRichEditor
                              initialHtml={item.value}
                              testid={`field-${item.key}`}
                              onCancel={() => setEditing(null)}
                              onSave={(html) => saveField(item.key, html)}
                            />
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-2">
            <Card className="border border-border rounded-none">
              <CardContent className="p-6 md:p-8">
                <h2 className="font-semibold text-lg mb-6">{t('contact.form_title')}</h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">{t('contact.name')} *</Label>
                      <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Jean Dupont" required className="rounded-none" data-testid="contact-name" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">{t('contact.email')} *</Label>
                      <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="email@exemple.com" required className="rounded-none" data-testid="contact-email" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="company">{t('contact.company')}</Label>
                      <Input id="company" value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} placeholder={t('contact.company_placeholder')} className="rounded-none" data-testid="contact-company" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">{t('contact.phone')}</Label>
                      <Input id="phone" type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+33 1 23 45 67 89" className="rounded-none" data-testid="contact-phone" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject">{t('contact.subject')} *</Label>
                    <Input id="subject" value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} placeholder={t('contact.subject_placeholder')} required className="rounded-none" data-testid="contact-subject" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">{t('contact.message')} *</Label>
                    <Textarea id="message" value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} placeholder={t('contact.message_placeholder')} required className="rounded-none min-h-[150px]" data-testid="contact-message" />
                  </div>

                  <Button type="submit" className="rounded-none" disabled={loading} data-testid="contact-submit">
                    {loading ? t('contact.sending') : t('contact.submit')}
                    <Send className="ml-2 w-4 h-4" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
